<template>
  <div class="lottery-card-wrapper">
    <transition 
      name="fade" 
      mode="out-in"
    >
      <HorizontalLotteryCard
        v-if="currentLayout === 'horizontal'"
        :lottery="lottery"
        :is-favorited="isFavoritedProp"
        :countdown="countdown"
        :favorite-icon="favoriteIcon"
        :favorite-filled-icon="favoriteFilledIcon"
        @favorite="toggleFavorite"
        @navigate="navigateToDetail"
      />
      <VerticalLotteryCard
        v-else
        :lottery="lottery"
        :is-favorited="isFavoritedProp"
        :countdown="countdown"
        :favorite-icon="favoriteIcon"
        :favorite-filled-icon="favoriteFilledIcon"
        :bet-closed="betClosed"
        @favorite="toggleFavorite"
        @bet="handleBet"
        @navigate="navigateToDetail"
      />
    </transition>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, onMounted, onUnmounted, watch } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useTheme } from '@/composables/useTheme';
import type { UpcomingDraw } from '@/types/lottery';
import { defineProps, defineEmits } from 'vue';

// Sub-components
import HorizontalLotteryCard from './cards/HorizontalLotteryCard.vue';
import VerticalLotteryCard from './cards/VerticalLotteryCard.vue';

const props = defineProps<{
  lottery: UpcomingDraw;
  layout?: 'vertical' | 'horizontal';
  isFavorited?: (draw: UpcomingDraw) => boolean;
}>();

const emit = defineEmits<{
  (e: 'bet'): void;
  (e: 'favorite'): void;
  (e: 'remove'): void;
}>();

const router = useRouter();
const route = useRoute();
const { getAssetPath } = useTheme();

// Derived Props
const currentLayout = computed(() => props.layout || 'horizontal');

// Assets
const favoriteIcon = getAssetPath('icons', 'favorite') || '/img/icons/favorite.svg';
const favoriteFilledIcon = getAssetPath('icons', 'favorite-filled') || '/img/icons/favorite-filled.svg';

// State
const betClosed = ref(false);
const countdown = ref({
  hours: 0,
  minutes: 0,
  seconds: 0
});

// Logic
const isFavoritedProp = computed(() => {
  return typeof props.isFavorited === 'function' ? props.isFavorited(props.lottery) : false;
});

const updateCountdown = () => {
  if (!props.lottery?.cutoff) {
    countdown.value = { hours: 0, minutes: 0, seconds: 0 };
    return;
  }
  const now = new Date();
  const cutoffRaw = props.lottery.cutoff as string | Date;
  const cutoffTime = typeof cutoffRaw === 'string'
    ? new Date(cutoffRaw.replace(' ', 'T'))
    : new Date(cutoffRaw);
  const diff = cutoffTime.getTime() - now.getTime();

  if (diff <= 0) {
    countdown.value = { hours: 0, minutes: 0, seconds: 0 };
    return;
  }

  countdown.value = {
    hours: Math.floor(diff / (1000 * 60 * 60)),
    minutes: Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60)),
    seconds: Math.floor((diff % (1000 * 60)) / 1000)
  };
};

let timer: number;

onMounted(() => {
  updateCountdown();
  timer = window.setInterval(updateCountdown, 1000);
});

onUnmounted(() => {
  if (timer) {
    clearInterval(timer);
  }
});

const toggleFavorite = () => {
  emit('favorite');
};

const handleBet = () => {
  emit('bet');
};

const navigateToDetail = () => {
  if (props.lottery?.game_slug) {
    router.push({ path: `/lottery/${props.lottery.game_slug}`, query: route.query });
  }
};

watch(
  () => [countdown.value.hours, countdown.value.minutes, countdown.value.seconds],
  ([hours, minutes, seconds]) => {
    if (hours === 0 && minutes === 0 && seconds === 0 && !betClosed.value) {
      betClosed.value = true;
      setTimeout(() => {
        emit('remove');
      }, 3000);
    }
  }
);
</script>

<style scoped>
.lottery-card-wrapper {
  width: 100%;
  height: 100%;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
